import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { Button, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, NumberInput, Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';

/** The query key the settings page invalidates, so a new field reaches this form. */
export const FIELDS_KEY = ['contact-fields'] as const;

/** What the form holds per key: the typed value, or undefined for "not set". */
export type FieldValues = Record<string, contacts.ContactFieldValue | undefined>;

/** The organisation's field definitions, shared by the form, the filter and the import preview. */
export function useContactFields() {
  const q = useQuery({
    queryKey: FIELDS_KEY,
    queryFn: () => contacts.listFields(),
    staleTime: 60_000,
    select: (r) => r.data
  });
  return { defs: q.data ?? [], loading: q.isPending, error: q.error, refetch: q.refetch };
}

/** How many of the active definitions hold a value: the tab's badge. */
export function countFilled(values: FieldValues, defs: contacts.ContactFieldDefinition[]): number {
  return defs.filter((d) => d.is_active && isSet(values[d.key])).length;
}

export function isSet(v: contacts.ContactFieldValue | undefined): boolean {
  return v !== undefined && v !== null && v !== '';
}

/** A value the field's input cannot express as typed, checked before the save so the refusal is local. */
export function localProblem(t: (k: string) => string, def: contacts.ContactFieldDefinition, v: contacts.ContactFieldValue | undefined): string | null {
  if (!isSet(v)) return null;
  const s = String(v).trim();
  switch (def.type) {
    case 'url':
      return /^https?:\/\/\S+$/i.test(s) ? null : t('contacts.form.fields.invalid.url');
    case 'date':
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? null : t('contacts.form.fields.invalid.date');
    case 'number':
      return Number.isFinite(Number(s)) ? null : t('contacts.form.fields.invalid.number');
    case 'select':
      return (def.options ?? []).some((o) => o.toLowerCase() === s.toLowerCase()) ? null : t('contacts.form.fields.invalid.select');
    default:
      return null;
  }
}

/**
 * One input per active field, by type.
 *
 * The values are the drawer's: this tab only renders and reports edits, so a
 * save from the Details tab carries what was typed here and the count on the
 * tab label stays right.
 */
export function FieldsTab({
  values,
  errors,
  onChange
}: {
  values: FieldValues;
  errors: Record<string, string>;
  onChange: (key: string, value: contacts.ContactFieldValue | undefined) => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const { defs, loading, error, refetch } = useContactFields();
  const active = defs.filter((d) => d.is_active);

  if (loading) {
    return (
      <Stack gap="md">
        <Skeleton height={54} />
        <Skeleton height={54} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="error" role="alert">
        <Stack gap="xs">
          <span>{t('contacts.form.fields.loadError')}</span>
          <div><Button size="xs" variant="default" onClick={() => refetch()}>{t('action.retry')}</Button></div>
        </Stack>
      </Alert>
    );
  }

  if (active.length === 0) {
    return (
      <EmptyState
        title={t('contacts.form.fields.empty')}
        description={t('contacts.form.fields.emptyHint')}
        action={
          <Button size="xs" variant="default" onClick={() => navigate('/settings/contact-fields')}>
            {t('contacts.form.fields.manage')}
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap="md">
      {active.map((d) => (
        <FieldInput key={d.id} def={d} value={values[d.key]} error={errors[d.key]} onChange={(v) => onChange(d.key, v)} />
      ))}
      <Text role="caption" c="var(--rr-color-text-muted)">
        {t('contacts.form.fields.manageHint')}{' '}
        <Link to="/settings/contact-fields">{t('contacts.form.fields.manage')}</Link>
      </Text>
    </Stack>
  );
}

function FieldInput({
  def,
  value,
  error,
  onChange
}: {
  def: contacts.ContactFieldDefinition;
  value: contacts.ContactFieldValue | undefined;
  error?: string;
  onChange: (v: contacts.ContactFieldValue | undefined) => void;
}) {
  const t = useT();
  const description = def.description ?? undefined;

  switch (def.type) {
    case 'number':
      return (
        <NumberInput
          label={def.label}
          description={description}
          error={error}
          value={typeof value === 'number' ? value : value === undefined || value === null || value === '' ? '' : Number(value)}
          onChange={(n) => onChange(n === '' ? undefined : n)}
          hideControls
        />
      );
    case 'boolean':
      // Off is also "not set" until the person touches it; the drawer only
      // sends a value for a key that was toggled or already held one.
      return (
        <Switch
          label={def.label}
          description={description}
          error={error}
          checked={value === true || value === 'true'}
          onChange={(checked) => onChange(checked)}
        />
      );
    case 'select':
      return (
        <Select
          label={def.label}
          description={description}
          error={error}
          value={isSet(value) ? String(value) : ''}
          onChange={(e) => onChange(e.currentTarget.value === '' ? undefined : e.currentTarget.value)}
          data={[
            { value: '', label: t('contacts.form.fields.unset') },
            ...(def.options ?? []).map((o) => ({ value: o, label: o }))
          ]}
        />
      );
    case 'date':
      return (
        <TextInput
          label={def.label}
          description={description}
          error={error}
          type="date"
          placeholder={t('contacts.form.fields.datePlaceholder')}
          value={isSet(value) ? String(value) : ''}
          onChange={(e) => onChange(e.currentTarget.value === '' ? undefined : e.currentTarget.value)}
        />
      );
    case 'url':
      return (
        <TextInput
          label={def.label}
          description={description}
          error={error}
          type="url"
          placeholder={t('contacts.form.fields.urlPlaceholder')}
          value={isSet(value) ? String(value) : ''}
          onChange={(e) => onChange(e.currentTarget.value === '' ? undefined : e.currentTarget.value)}
        />
      );
    default:
      return (
        <TextInput
          label={def.label}
          description={description}
          error={error}
          value={isSet(value) ? String(value) : ''}
          onChange={(e) => onChange(e.currentTarget.value === '' ? undefined : e.currentTarget.value)}
        />
      );
  }
}
